/**
 * Featured Collection Tabs Component v2.1
 * Optimized tabbed collections with accessibility
 */

class FeaturedCollectionTabs {
  constructor(container) {
    if (!container?.nodeType) throw new Error('Invalid container');

    this.container = container;
    this.sectionId = container.dataset.sectionId || `tabs-${Date.now()}`;
    this.tabButtons = container.querySelectorAll('.collection-tabs__nav-item');
    this.tabPanels = container.querySelectorAll('.collection-tabs__panel');
    this.navContainer = container.querySelector('.collection-tabs__nav');
    this.navWrapper = container.querySelector('[data-tabs-nav-wrapper]');
    this.scrollContainer = container.querySelector('[data-tabs-scroll-container]');
    this.scrollLeftBtn = container.querySelector('[data-scroll-button="left"]');
    this.scrollRightBtn = container.querySelector('[data-scroll-button="right"]');

    this.activeIndex = 0;
    this.isDestroyed = false;
    this.resizeTimerId = null;

    // Store bound handlers for cleanup
    this.handlers = {
      click: (e) => this.handleClick(e),
      keydown: (e) => this.handleKeydown(e),
      scrollLeft: () => this.scrollContainer?.scrollBy({ left: -250, behavior: 'smooth' }),
      scrollRight: () => this.scrollContainer?.scrollBy({ left: 250, behavior: 'smooth' }),
      resize: null
    };

    // Store RAF IDs and timers for cleanup
    this.rafIds = new Set();
    this.timers = new Set();

    if (this.validateElements()) this.init();
  }

  init() {
    if (this.isDestroyed) return;

    this.setupEvents();
    this.setupAccessibility();
    this.setupAlignment();
    this.setActiveTab(0);

    this.container.classList.add('collection-tabs--initialized');
  }

  setupEvents() {
    this.navContainer.addEventListener('click', this.handlers.click);
    this.navContainer.addEventListener('keydown', this.handlers.keydown);

    if (this.scrollLeftBtn && this.scrollRightBtn) {
      this.scrollLeftBtn.addEventListener('click', this.handlers.scrollLeft);
      this.scrollRightBtn.addEventListener('click', this.handlers.scrollRight);
    }
  }

  handleClick(e) {
    const button = e.target.closest('.collection-tabs__nav-item');
    if (!button) return;

    e.preventDefault();
    const index = Array.from(this.tabButtons).indexOf(button);
    if (index !== -1 && index !== this.activeIndex) this.switchTab(index);
  }

  handleKeydown(e) {
    const button = e.target.closest('.collection-tabs__nav-item');
    if (!button) return;

    const index = Array.from(this.tabButtons).indexOf(button);
    const total = this.tabButtons.length;
    const keyMap = {
      'ArrowRight': (index + 1) % total,
      'ArrowDown': (index + 1) % total,
      'ArrowLeft': (index - 1 + total) % total,
      'ArrowUp': (index - 1 + total) % total,
      'Home': 0,
      'End': total - 1,
      'Enter': index,
      ' ': index
    };

    const newIndex = keyMap[e.key];
    if (newIndex === undefined) return;

    e.preventDefault();
    if (newIndex !== index) this.tabButtons[newIndex].focus();
    this.switchTab(newIndex);
  }

  setupAlignment() {
    const updateAlignment = () => {
      if (this.isDestroyed || !this.navContainer || !this.navWrapper) return;
      const isScrollable = this.navContainer.scrollWidth > this.navContainer.clientWidth;
      this.navContainer.style.justifyContent = isScrollable ? 'flex-start' : 'center';
      this.navWrapper.classList.toggle('has-overflow', isScrollable);
    };

    this.handlers.resize = () => {
      if (this.isDestroyed) return;

      // Clear existing timer if any
      if (this.resizeTimerId) {
        clearTimeout(this.resizeTimerId);
        this.timers.delete(this.resizeTimerId);
      }

      this.resizeTimerId = setTimeout(() => {
        if (!this.isDestroyed) updateAlignment();
        this.resizeTimerId = null;
      }, 100);

      this.timers.add(this.resizeTimerId);
    };

    window.addEventListener('resize', this.handlers.resize, { passive: true });

    const rafId = requestAnimationFrame(updateAlignment);
    this.rafIds.add(rafId);
  }


  switchTab(targetIndex) {
    if (targetIndex === this.activeIndex || targetIndex < 0 || targetIndex >= this.tabButtons.length) return;

    this.activeIndex = targetIndex;

    const rafId = requestAnimationFrame(() => {
      if (this.isDestroyed) return;

      this.tabButtons.forEach((btn, i) => {
        const isActive = i === targetIndex;
        btn.classList.toggle('collection-tabs__nav-item--active', isActive);
        btn.setAttribute('aria-selected', isActive);
        btn.tabIndex = isActive ? 0 : -1;
      });

      this.tabPanels.forEach((panel, i) => {
        const isActive = i === targetIndex;
        panel.classList.toggle('collection-tabs__panel--hidden', !isActive);
        panel.setAttribute('aria-hidden', !isActive);
      });
    });
    this.rafIds.add(rafId);
  }

  setupAccessibility() {
    this.navContainer.setAttribute('role', 'tablist');

    this.tabButtons.forEach((btn, i) => {
      btn.setAttribute('role', 'tab');
      btn.id = `tab-${this.sectionId}-${i}`;
      btn.setAttribute('aria-controls', `panel-${this.sectionId}-${i}`);
    });

    this.tabPanels.forEach((panel, i) => {
      panel.setAttribute('role', 'tabpanel');
      panel.id = `panel-${this.sectionId}-${i}`;
      panel.setAttribute('aria-labelledby', `tab-${this.sectionId}-${i}`);
    });
  }

  validateElements() {
    return this.tabButtons.length > 0 &&
           this.tabButtons.length === this.tabPanels.length &&
           this.navContainer;
  }

  setActiveTab(index) {
    if (index >= 0 && index < this.tabButtons.length) this.switchTab(index);
  }

  getActiveTab() {
    return this.activeIndex;
  }

  destroy() {
    if (this.isDestroyed) return;

    this.isDestroyed = true;

    // Remove all event listeners
    if (this.navContainer) {
      this.navContainer.removeEventListener('click', this.handlers.click);
      this.navContainer.removeEventListener('keydown', this.handlers.keydown);
    }

    if (this.scrollLeftBtn && this.handlers.scrollLeft) {
      this.scrollLeftBtn.removeEventListener('click', this.handlers.scrollLeft);
    }

    if (this.scrollRightBtn && this.handlers.scrollRight) {
      this.scrollRightBtn.removeEventListener('click', this.handlers.scrollRight);
    }

    if (this.handlers.resize) {
      window.removeEventListener('resize', this.handlers.resize);
    }

    // Clear all timers
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();

    // Cancel all RAF callbacks
    this.rafIds.forEach(id => cancelAnimationFrame(id));
    this.rafIds.clear();

    // Clear class
    this.container.classList.remove('collection-tabs--initialized');

    // Null out all references
    this.container = null;
    this.tabButtons = null;
    this.tabPanels = null;
    this.navContainer = null;
    this.navWrapper = null;
    this.scrollContainer = null;
    this.scrollLeftBtn = null;
    this.scrollRightBtn = null;
    this.handlers = null;
  }

  static initAll() {
    return Array.from(document.querySelectorAll('[data-section-type="featured-collection-tabs"]'))
      .filter(container => !container.featuredCollectionTabs)
      .map(container => {
        try {
          const instance = new FeaturedCollectionTabs(container);
          container.featuredCollectionTabs = instance;
          return instance;
        } catch (error) {
          console.error('FeaturedCollectionTabs init failed:', error);
          return null;
        }
      })
      .filter(Boolean);
  }
}

// Auto-initialize
const init = () => FeaturedCollectionTabs.initAll();
document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', init)
  : init();

// Shopify Theme Editor Support
document.addEventListener('shopify:section:load', (e) => {
  const container = e.target.querySelector('[data-section-type="featured-collection-tabs"]');
  if (!container) return;

  setTimeout(() => {
    try {
      const instance = new FeaturedCollectionTabs(container);
      container.featuredCollectionTabs = instance;
    } catch (error) {
      console.error('FeaturedCollectionTabs init failed:', error);
    }
  }, 100);
});

document.addEventListener('shopify:section:unload', (e) => {
  const container = e.target.querySelector('[data-section-type="featured-collection-tabs"]');
  if (container?.featuredCollectionTabs) {
    container.featuredCollectionTabs.destroy();
    delete container.featuredCollectionTabs;
  }
});

window.FeaturedCollectionTabs = FeaturedCollectionTabs;
